import React, { useState } from 'react';
import { Form, Button, Card, Alert, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

const ReferralForm = () => {
  const [formData, setFormData] = useState({
    referredName: '',
    referredEmail: '',
    referredCompany: ''
  });
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.referredName.trim()) {
      newErrors.referredName = 'Name is required';
    } else if (formData.referredName.trim().length < 2) {
      newErrors.referredName = 'Name must be at least 2 characters';
    }
    
    if (!formData.referredEmail.trim()) {
      newErrors.referredEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.referredEmail)) {
      newErrors.referredEmail = 'Please enter a valid email address';
    }
    
    if (!formData.referredCompany.trim()) {
      newErrors.referredCompany = 'Company name is required';
    } else if (formData.referredCompany.trim().length < 2) {
      newErrors.referredCompany = 'Company name must be at least 2 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      await axios.post('/referrals/invite', formData);
      
      toast.success('Invitation sent successfully!');
      setShowSuccessModal(true);
      
      // Reset form
      setFormData({
        referredName: '',
        referredEmail: '',
        referredCompany: ''
      });
      
    } catch (error) {
      console.error('Error sending invitation:', error);
      
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data?.errors) {
        // Handle validation errors from server
        const serverErrors = {};
        error.response.data.errors.forEach(err => {
          serverErrors[err.path] = err.msg;
        });
        setErrors(serverErrors);
        toast.error('Please fix the errors in the form');
      } else {
        toast.error('Failed to send invitation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card>
        <Card.Header>
          <h4 className="mb-0">🚕 Refer a Colleague</h4>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            <strong>How it works:</strong> Invite your colleague to try Taxi-Local.uk for 1 month for just £1. 
            When confirmed as full Member/Subscription, you'll receive a free month worth £25!
          </Alert>
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Colleague's Name *</Form.Label>
              <Form.Control
                type="text"
                name="referredName"
                value={formData.referredName}
                onChange={handleChange}
                placeholder="Enter their full name"
                isInvalid={!!errors.referredName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.referredName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Colleague's Email *</Form.Label>
              <Form.Control
                type="email"
                name="referredEmail"
                value={formData.referredEmail}
                onChange={handleChange}
                placeholder="Enter their email address"
                isInvalid={!!errors.referredEmail}
              />
              <Form.Control.Feedback type="invalid">
                {errors.referredEmail}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Company Name *</Form.Label>
              <Form.Control
                type="text"
                name="referredCompany"
                value={formData.referredCompany}
                onChange={handleChange}
                placeholder="Enter their company name"
                isInvalid={!!errors.referredCompany}
              />
              <Form.Control.Feedback type="invalid">
                {errors.referredCompany}
              </Form.Control.Feedback>
            </Form.Group>

            <Button 
              type="submit" 
              variant="primary" 
              disabled={loading}
              className="w-100"
            >
              {loading ? 'Sending Invitation...' : 'Send Invitation'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>✅ Invitation Sent!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Your colleague will receive an email invitation to try Taxi-Local.uk for 1 month for just £1.</p>
          <p><strong>What happens next:</strong></p>
          <ul>
            <li>They'll receive an email with a special signup link</li>
            <li>They can sign up and try the platform for £1</li>
            <li>When confirmed as full Member/Subscription, you'll get a £25 reward!</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSuccessModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReferralForm;
